import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { settingsService, getErrorMessage } from '@/services';
import type { ProfileUpdateRequest, PasswordChangeRequest, UserPreferences } from '@/types';
import { useAuthStore } from '@/store/auth';

export function useProfile() {
  return useQuery({
    queryKey: ['profile'],
    queryFn: () => settingsService.getProfile(),
  });
}

export function useUpdateProfileSettings() {
  const queryClient = useQueryClient();
  const { setUser } = useAuthStore();

  return useMutation({
    mutationFn: (data: ProfileUpdateRequest) => settingsService.updateProfile(data),
    onSuccess: (updatedUser) => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      setUser({
        ...useAuthStore.getState().user!,
        first_name: updatedUser.first_name,
        last_name: updatedUser.last_name,
        full_name: updatedUser.full_name,
        phone: updatedUser.phone,
        profile_picture: updatedUser.profile_picture,
        date_of_birth: updatedUser.date_of_birth,
        gender: updatedUser.gender,
      });
      toast.success('Profile updated successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useChangePasswordSettings() {
  return useMutation({
    mutationFn: (data: PasswordChangeRequest) => settingsService.changePassword(data),
    onSuccess: () => {
      toast.success('Password changed successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function usePreferences() {
  return useQuery({
    queryKey: ['preferences'],
    queryFn: () => settingsService.getPreferences(),
  });
}

export function useUpdatePreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<UserPreferences>) => settingsService.updatePreferences(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['preferences'] });
      toast.success('Preferences updated successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useUploadProfilePicture() {
  const queryClient = useQueryClient();
  const { setUser } = useAuthStore();

  return useMutation({
    mutationFn: ({
      formData,
      onProgress,
    }: {
      formData: FormData;
      onProgress?: (percent: number) => void;
    }) => settingsService.uploadProfilePicture(formData, onProgress),
    onSuccess: (updatedUser) => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      // The header avatar reads the auth store, not the ['profile'] query.
      const current = useAuthStore.getState().user;
      if (current && updatedUser?.profile_picture !== undefined) {
        setUser({ ...current, profile_picture: updatedUser.profile_picture });
      }
      toast.success('Profile picture updated successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
